/**
 * Upgrade click decision for Plan & Usage / classic pricing
 * (UpdatedPricingPageV2). Ticket: Free Forever -> paid plan must call
 * request-subscription. Salla and PayPal leave this flow before an API call.
 */

export const PAYPAL_EXCLUDED_UPGRADE_SOURCES = [
  'WIX',
  'WOOCOMMERCE',
  'SHOPIFY',
  'DISCONNECT',
  'SALLA',
  'SHOPIFY_',
] as const

export const CUSTOM_PLAN_NON_PAYPAL_SOURCES = ['SHOPIFY', 'DISCONNECT', 'SHOPIFY_'] as const

export const matchesPaypalExcludedSource = (source: string, rules: readonly string[]) =>
  rules.some((rule) => (rule.endsWith('_') ? source.startsWith(rule) : source === rule))

/** Account sources that open the PayPal modal instead of request/change-subscription. */
export const shouldUsePaypalForUpgradeSource = (source?: string | null) =>
  !matchesPaypalExcludedSource(source || '', PAYPAL_EXCLUDED_UPGRADE_SOURCES)

/** Custom-plan PayPal check uses the plan source, not the account source. */
export const shouldUsePaypalForCustomPlan = (planSource?: string | null) =>
  !matchesPaypalExcludedSource(
    String(planSource || '').toUpperCase(),
    CUSTOM_PLAN_NON_PAYPAL_SOURCES
  )

export const isFreeBillingPlan = (
  billingFrequency?: string | null,
  priceValue?: number | null
) => billingFrequency === 'FREE_FOREVER' || Number(priceValue) === 0

/** Same trim as UpdatedPricingPageV2 `currentSubscriptionId`. Empty becomes null. */
export const normalizeSubscriptionId = (value?: string | null) => {
  if (!value) return null
  const trimmed = String(value).trim()
  return trimmed || null
}

export type PaidUpgradeApi = 'request-subscription' | 'change-subscription'

/**
 * Used only after the click already has a subscription id and a user plan,
 * and the target is a paid plan. Free Forever still calls request-subscription.
 */
export const selectApiForUpgradeWithSubscriptionId = (
  currentBillingFrequency?: string | null
): PaidUpgradeApi =>
  currentBillingFrequency === 'FREE_FOREVER' ? 'request-subscription' : 'change-subscription'

export const requestSubscriptionPayload = (
  planId: number | undefined,
  platformId: number | null
) => ({
  plan_id: planId,
  platform_id: platformId ?? null,
})

export const changeSubscriptionPayload = (
  planId: number | undefined,
  platformId: number | null,
  subscriptionId: string | null
) => ({
  plan_id: planId,
  platform_id: Number(platformId) ?? null,
  subscription_id: subscriptionId,
})

export type UpgradeClickAction =
  | 'salla-noop'
  | 'paypal-modal'
  | 'confirm-downgrade-to-free'
  | 'request-subscription'
  | 'change-subscription'
  | 'billed-manually'

export type UpgradeClickInput = {
  /** standard = non-custom Upgrade button. custom = custom-plan Upgrade button. */
  variant: 'standard' | 'custom'
  source?: string | null
  customPlanSource?: string | null
  targetBillingFrequency?: string | null
  targetPriceValue?: number | null
  targetDescription?: string | null
  currentBillingFrequency?: string | null
  currentSubscriptionId?: string | null
  hasUserPlan: boolean
}

/**
 * Mirrors the Upgrade onClick order in UpdatedPricingPageV2.
 * Downgrade buttons are a separate handler and are not decided here.
 */
export const resolveUpgradeClickAction = (input: UpgradeClickInput): UpgradeClickAction => {
  const isSallaSource = input.source === 'SALLA'
  const isFreePlan = isFreeBillingPlan(input.targetBillingFrequency, input.targetPriceValue)
  const isCustomPlan = input.variant === 'custom'
  const subscriptionId = normalizeSubscriptionId(input.currentSubscriptionId)

  if (isSallaSource && !isFreePlan) return 'salla-noop'

  if (isCustomPlan && shouldUsePaypalForCustomPlan(input.customPlanSource)) {
    return 'paypal-modal'
  }

  if (
    !isCustomPlan &&
    shouldUsePaypalForUpgradeSource(input.source) &&
    input.targetBillingFrequency !== 'FREE_FOREVER'
  ) {
    return 'paypal-modal'
  }

  if (isFreePlan) return 'confirm-downgrade-to-free'

  if (!subscriptionId) return 'request-subscription'

  if (!input.hasUserPlan && input.targetBillingFrequency === 'FREE_FOREVER') {
    return 'confirm-downgrade-to-free'
  }

  if (
    !input.hasUserPlan ||
    (input.targetBillingFrequency !== 'FREE_FOREVER' && !subscriptionId)
  ) {
    return 'request-subscription'
  }

  if (isCustomPlan && input.targetDescription === 'BILLED_MANUALLY') {
    return 'billed-manually'
  }

  return selectApiForUpgradeWithSubscriptionId(input.currentBillingFrequency)
}
